import { EntityDetailSkeleton } from "@/components/layout/entity-detail-skeleton"
import { useOrganisation, useDeleteOrganisation } from "@/hooks/use-admin-organisations"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Trash2, Pencil } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface OrganisationDetailProps {
  organisationId: string
  onEdit: () => void
  onClose: () => void
}

export function OrganisationDetail({ organisationId, onEdit, onClose }: OrganisationDetailProps) {
  const { data: organisation, isLoading } = useOrganisation(organisationId)
  const deleteOrganisation = useDeleteOrganisation()

  const handleDelete = async () => {
    await deleteOrganisation.mutateAsync(organisationId)
    onClose()
  }

  if (isLoading) {
    return <EntityDetailSkeleton variant="profile" withPageContainer={false} ariaLabel="Loading organisation" />
  }

  if (!organisation) {
    return <div className="text-center py-8 text-muted-foreground">Organisation not found</div>
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{organisation.name}</CardTitle>
              <CardDescription>Organisation Details</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="size-4 mr-2" />
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="size-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the organisation
                      "{organisation.name}".
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">Name</Label>
            <p className="text-sm mt-1">{organisation.name}</p>
          </div>

          {organisation.description && (
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Description</Label>
              <p className="text-sm mt-1">{organisation.description}</p>
            </div>
          )}

          {organisation.website && (
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Website</Label>
              <p className="text-sm mt-1">
                <a href={organisation.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {organisation.website}
                </a>
              </p>
            </div>
          )}

          {organisation.industry && (
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Industry</Label>
              <p className="text-sm mt-1">{organisation.industry}</p>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Slug</Label>
            <p className="text-sm mt-1 font-mono">{organisation.slug}</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Created</Label>
            <p className="text-sm mt-1">{new Date(organisation.created_at).toLocaleString()}</p>
          </div>

          <div>
            <Label className="text-sm font-medium text-muted-foreground">Last updated</Label>
            <p className="text-sm mt-1">{new Date(organisation.updated_at).toLocaleString()}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

